import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Eraser, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { SignaturePad } from "@/components/SignaturePad";
import { API, api, errMsg } from "@/lib/api";
import { fmtDate, fmtQty } from "@/lib/format";

const KV = ({ label, value }) => value ? <div><div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div><div className="text-sm text-slate-900">{value}</div></div> : null;
const RESULT = { completada: "Completada", pendiente: "Pendiente" };
const STATE_MSG = { firmada: "Esta orden de servicio ya fue firmada.", expirada: "Este enlace de firma expiró. Solicita uno nuevo a TSSMX.", inactiva: "Este enlace ya no está disponible." };

const Header = () => (
  <header className="bg-slate-900 px-5 py-4 text-white">
    <div className="text-xl font-bold tracking-wide">TSSMX</div>
    <div className="text-xs text-slate-300">Tracker &amp; Security Systems MX</div>
  </header>
);

const Doc = ({ d, token }) => (
  <div className="space-y-5">
    <div className="grid grid-cols-2 gap-3">
      <KV label="Folio" value={d.folio} /><KV label="Fecha del servicio" value={fmtDate(d.service_date)} />
      <KV label="Cliente" value={d.client_name} /><KV label="Proyecto" value={d.project_name} />
      <KV label="Unidad" value={d.business_unit_name} /><KV label="Tipo de servicio" value={d.service_type_name} />
      <KV label="Técnico" value={d.technician_name} /><KV label="Resultado" value={RESULT[d.result] || d.result} />
    </div>
    <KV label="Dirección" value={d.service_address} />
    <KV label="Descripción / alcance" value={d.scope} />
    <KV label="Observaciones" value={[d.notes, d.technician_notes].filter(Boolean).join(" · ")} />
    {d.equipment.length > 0 && <div><div className="mb-1 text-[11px] uppercase tracking-wider text-slate-500">Equipos instalados / entregados</div>
      <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 text-sm">{d.equipment.map((e, i) => (
        <li key={i} className="flex justify-between gap-2 px-3 py-2" data-testid={`sign-equipment-${i}`}><span>{[e.type_name, e.brand, e.model].filter(Boolean).join(" · ") || e.description}</span><span className="font-mono">{fmtQty(e.quantity)} {e.unit}</span></li>
      ))}</ul></div>}
    {d.evidences.length > 0 && <div><div className="mb-1 text-[11px] uppercase tracking-wider text-slate-500">Evidencias</div>
      <div className="grid grid-cols-3 gap-2">{d.evidences.map((e) => <img key={e.index} src={`${API}/public/sign/${token}/evidence/${e.index}`} alt={e.caption || e.category} className="aspect-square w-full rounded-md object-cover" loading="lazy" />)}</div></div>}
  </div>
);

export default function SignPage() {
  const { token } = useParams();
  const pad = useRef(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [name, setName] = useState("");
  const [ok, setOk] = useState(false);
  const [inked, setInked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => { api.get(`/public/sign/${token}`).then((r) => setData(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  const sign = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/public/sign/${token}`, { signer_name: name, image_data: pad.current.toDataURL(), document_hash: data.document_hash, accepted: ok });
      setDone(r);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const msg = done ? null : err || (data && data.state !== "pendiente" ? STATE_MSG[data.state] : null);
  return (
    <div className="min-h-screen bg-slate-100 text-slate-900" data-testid="sign-page">
      <Header />
      <main className="mx-auto max-w-xl space-y-5 p-4 pb-16">
        <h1 className="text-lg font-bold tracking-wide">CONFIRMACIÓN DE SERVICIO</h1>
        {!data && !err && <Loader2 className="mx-auto h-6 w-6 animate-spin" />}
        {msg && <div className="rounded-lg border border-slate-300 bg-white p-4 text-sm" data-testid="sign-state-message">{msg}</div>}
        {done && <div className="space-y-2 rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm" data-testid="sign-done"><CheckCircle2 className="h-6 w-6 text-emerald-600" />Gracias, {done.signer_name}. La orden {done.folio} quedó firmada.</div>}
        {data?.state === "pendiente" && !done && (
          <>
            <section className="rounded-xl bg-white p-4 shadow-sm" data-testid="sign-document"><Doc d={data.document} token={token} /></section>
            <section className="space-y-4 rounded-xl bg-white p-4 shadow-sm">
              <label className="flex gap-3 text-sm"><Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid="sign-accept-checkbox" />
                Confirmo que he recibido el servicio descrito en esta orden de servicio y que la información mostrada corresponde al trabajo realizado.</label>
              <div className="space-y-1"><div className="text-[11px] uppercase tracking-wider text-slate-500">Nombre del firmante *</div>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="h-12 bg-white text-base text-slate-900" data-testid="sign-name-input" /></div>
              <div className="space-y-1"><div className="text-[11px] uppercase tracking-wider text-slate-500">Firma</div>
                <div className="rounded-xl border-2 border-dashed border-slate-300 bg-white p-2"><SignaturePad ref={pad} onInk={setInked} /></div></div>
              <div className="grid grid-cols-[auto_1fr] gap-2">
                <Button variant="outline" className="h-14" onClick={() => pad.current.clear()} data-testid="sign-clear-button"><Eraser className="h-5 w-5" /></Button>
                <Button className="h-14 bg-slate-900 text-base text-white hover:bg-slate-800" disabled={!ok || !inked || name.trim().length < 2 || busy} onClick={sign} data-testid="sign-submit-button">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confirmar y firmar</Button>
              </div>
              <p className="flex items-center gap-1 text-xs text-slate-500"><ShieldCheck className="h-3.5 w-3.5" />Enlace seguro de un solo uso. No se requiere cuenta.</p>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
